import {
  type Constraint,
  type ConstraintId,
  residualCount,
  residualsOf,
  type Slot,
  SlotKind,
  slotsOf,
  weightOf,
} from '@/domain/constraints';
import { asPoint } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { addTo, type Matrix, set } from '@/domain/math/linear';
import { type Entity, type EntityId, type SceneDocument, updateEntity } from '@/domain/model';

import { type Scene, valueOf } from '../scene/Scene';

import { type Cluster, clustersAffecting, findClusters } from './clusters';
import {
  type LeastSquaresProblem,
  solveLeastSquares,
  type SolveOptions,
  type SolveResult,
  SolveStatus,
} from './levenbergMarquardt';

/**
 * Assembling and solving a constraint cluster.
 *
 * ## What is a variable
 *
 * Only **free points** are variables. That is the composition rule of ADR-0003 made
 * concrete: Tier 2 may never write to a value Tier 1 derived, because doing so would
 * replace an exact construction with a converged approximation of itself. A derived point
 * that a constraint happens to touch is a *constant* in the system — which is useful in
 * its own right, since it lets a free circle be constrained tangent to a derived one.
 *
 * A constraint whose entities cannot be resolved to points at all is reported as skipped
 * rather than silently dropped: a constraint the user created and that quietly does
 * nothing is worse than one that says why.
 */

export interface DragAnchor {
  /** The free point being dragged. */
  readonly point: EntityId;
  readonly target: Vec2;
  /**
   * Anchor strength relative to the real constraints.
   *
   * Deliberately small. The anchor exists to choose *which* of the many configurations
   * satisfying the constraints the solver lands on — the one nearest the pointer. Making
   * it comparable in weight would let the pointer break constraints instead.
   */
  readonly weight?: number;
}

export interface ClusterSolution {
  /** New positions for the free points the solver moved. */
  readonly positions: ReadonlyMap<EntityId, Vec2>;
  readonly status: SolveStatus;
  readonly error: number;
  readonly skipped: readonly ConstraintId[];
  /** Degrees of freedom minus equations. Negative means over-constrained. */
  readonly slack: number;
}

const DEFAULT_ANCHOR_WEIGHT = 0.05;

/* ------------------------------------------------------------------- resolution */

/** Expands one slot into the point entities its residual needs. */
function resolveSlot(document: SceneDocument, slot: Slot): readonly EntityId[] | null {
  const entity = document.entities.get(slot.entity);
  if (entity === undefined) return null;
  const source = entity.source;

  switch (slot.kind) {
    case SlotKind.Point:
      return isPointSource(source.op) ? [slot.entity] : null;

    case SlotKind.Linear:
      if (source.op === 'segment' || source.op === 'line') return [source.a, source.b];
      if (source.op === 'ray') return [source.origin, source.through];
      if (source.op === 'vector') return [source.from, source.to];
      return null;

    case SlotKind.Circle:
      // Only a centre-and-rim circle exposes a rim point to move. A fixed-radius circle
      // has no second handle, and a three-point circle is fully derived — neither can
      // participate as a variable, so the constraint is reported rather than mis-solved.
      if (source.op === 'circle-through') return [source.center, source.through];
      return null;
  }
}

function isPointSource(op: string): boolean {
  return (
    op === 'free-point' ||
    op === 'locus-point' ||
    op === 'midpoint' ||
    op === 'segment-midpoint' ||
    op === 'intersection' ||
    op === 'projected-point' ||
    op === 'reflected-point' ||
    op === 'rotated-point' ||
    op === 'translated-point' ||
    op === 'scaled-point' ||
    op === 'circumcenter' ||
    op === 'incenter' ||
    op === 'centroid' ||
    op === 'orthocenter' ||
    op === 'circle-center'
  );
}

interface ResolvedConstraint {
  readonly constraint: Constraint;
  readonly points: readonly EntityId[];
}

function resolve(document: SceneDocument, constraint: Constraint): ResolvedConstraint | null {
  const points: EntityId[] = [];
  for (const slot of slotsOf(constraint)) {
    const resolved = resolveSlot(document, slot);
    if (resolved === null) return null;
    points.push(...resolved);
  }
  return { constraint, points };
}

/** True when this entity is a free point the solver is allowed to move. */
function isVariable(entity: Entity | undefined): boolean {
  return entity?.source.op === 'free-point' && !entity.locked;
}

/* ---------------------------------------------------------------------- solving */

/**
 * Solves one cluster and returns the free-point positions it settled on.
 *
 * The document is not modified — the caller decides whether to accept the result, which
 * is what lets a drag preview run the solver without committing anything to history.
 */
export function solveCluster(
  document: SceneDocument,
  scene: Scene,
  cluster: Cluster,
  anchor?: DragAnchor,
  options?: SolveOptions
): ClusterSolution {
  const resolved: ResolvedConstraint[] = [];
  const skipped: ConstraintId[] = [];

  for (const constraint of cluster.constraints) {
    const result = resolve(document, constraint);
    if (result === null) {
      skipped.push(constraint.id);
      continue;
    }
    resolved.push(result);
  }

  // Current position of every point involved, whether variable or constant.
  const positions = new Map<EntityId, Vec2>();
  const variableIndex = new Map<EntityId, number>();
  const variableOrder: EntityId[] = [];

  const remember = (id: EntityId): void => {
    if (positions.has(id)) return;
    const point = asPoint(valueOf(scene, id));
    positions.set(id, point ?? vec.ZERO);
    if (isVariable(document.entities.get(id)) && !variableIndex.has(id)) {
      variableIndex.set(id, variableOrder.length * 2);
      variableOrder.push(id);
    }
  };

  for (const item of resolved) {
    for (const id of item.points) remember(id);
  }
  if (anchor !== undefined) remember(anchor.point);

  const variableCount = variableOrder.length * 2;

  let equationCount = 0;
  for (const item of resolved) equationCount += residualCount(item.constraint);
  const anchorRows = anchor !== undefined && variableIndex.has(anchor.point) ? 2 : 0;
  const rowCount = equationCount + anchorRows;

  const slack = variableCount - equationCount;

  if (variableCount === 0 || rowCount === 0) {
    return {
      positions: new Map(),
      status: SolveStatus.Converged,
      error: 0,
      skipped,
      slack,
    };
  }

  // Scratch position map reused every evaluation; the alternative is allocating a Map
  // per LM iteration, inside a drag loop.
  const working = new Map(positions);
  const scratch: Vec2[] = [];

  const problem: LeastSquaresProblem = {
    variableCount,
    residualCount: rowCount,

    evaluate(x, residuals, jacobian) {
      for (let i = 0; i < variableOrder.length; i++) {
        const id = variableOrder[i];
        if (id === undefined) continue;
        working.set(id, { x: x[i * 2] ?? 0, y: x[i * 2 + 1] ?? 0 });
      }

      zeroJacobian(jacobian);
      let row = 0;

      for (const item of resolved) {
        scratch.length = 0;
        for (const id of item.points) scratch.push(working.get(id) ?? vec.ZERO);

        const weight = weightOf(item.constraint);
        const terms = residualsOf(item.constraint, scratch);

        for (const term of terms) {
          residuals[row] = term.value * weight;

          term.gradient.forEach((gradient, index) => {
            const pointId = item.points[index];
            if (pointId === undefined) return;
            const column = variableIndex.get(pointId);
            if (column === undefined) return; // a constant, not a variable

            // A point can appear more than once in a constraint, so accumulate.
            addTo(jacobian, row, column, gradient.x * weight);
            addTo(jacobian, row, column + 1, gradient.y * weight);
          });
          row++;
        }
      }

      if (anchorRows > 0 && anchor !== undefined) {
        const column = variableIndex.get(anchor.point);
        const current = working.get(anchor.point) ?? vec.ZERO;
        const weight = anchor.weight ?? DEFAULT_ANCHOR_WEIGHT;

        residuals[row] = (current.x - anchor.target.x) * weight;
        if (column !== undefined) set(jacobian, row, column, weight);
        row++;

        residuals[row] = (current.y - anchor.target.y) * weight;
        if (column !== undefined) set(jacobian, row, column + 1, weight);
        row++;
      }
    },
  };

  const initial = new Float64Array(variableCount);
  for (let i = 0; i < variableOrder.length; i++) {
    const id = variableOrder[i];
    if (id === undefined) continue;
    const point = positions.get(id) ?? vec.ZERO;
    initial[i * 2] = point.x;
    initial[i * 2 + 1] = point.y;
  }

  const result: SolveResult = solveLeastSquares(problem, initial, options ?? {});

  const solved = new Map<EntityId, Vec2>();
  for (let i = 0; i < variableOrder.length; i++) {
    const id = variableOrder[i];
    if (id === undefined) continue;
    const x = result.x[i * 2] ?? 0;
    const y = result.x[i * 2 + 1] ?? 0;
    // Never let a diverged solve write NaN into the document.
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    solved.set(id, { x, y });
  }

  return { positions: solved, status: result.status, error: result.error, skipped, slack };
}

function zeroJacobian(jacobian: Matrix): void {
  jacobian.data.fill(0);
}

/* --------------------------------------------------------------- orchestration */

export interface ConstraintPass {
  readonly document: SceneDocument;
  /** Free points the solver moved. */
  readonly moved: ReadonlySet<EntityId>;
  readonly solutions: readonly ClusterSolution[];
}

/**
 * Solves every cluster touched by `changed`, and returns the updated document.
 *
 * Runs *after* the Tier-1 sweep, never instead of it: the constructive DAG has already
 * produced exact values for everything derived, and this pass only nudges the free points
 * that carry relational constraints. Clusters untouched by the change are not solved at
 * all — a scene with five thousand entities and one constrained triangle solves a
 * six-variable system.
 */
export function applyConstraints(
  document: SceneDocument,
  scene: Scene,
  changed: ReadonlySet<EntityId>,
  anchor?: DragAnchor,
  options?: SolveOptions
): ConstraintPass {
  const constraints = [...document.constraints.values()];
  if (constraints.length === 0) {
    return { document, moved: new Set(), solutions: [] };
  }

  const clusters = findClusters(constraints);
  const affected =
    changed.size === 0 && anchor === undefined ? clusters : clustersAffecting(clusters, changed);

  // A drag anchor implies its own cluster even if nothing else changed.
  const targets =
    anchor === undefined
      ? affected
      : dedupeClusters([...affected, ...clustersAffecting(clusters, new Set([anchor.point]))]);

  if (targets.length === 0) {
    return { document, moved: new Set(), solutions: [] };
  }

  let next = document;
  const moved = new Set<EntityId>();
  const solutions: ClusterSolution[] = [];

  for (const cluster of targets) {
    const solution = solveCluster(next, scene, cluster, anchor, options);
    solutions.push(solution);

    for (const [id, position] of solution.positions) {
      const entity = next.entities.get(id);
      if (entity?.source.op !== 'free-point') continue;
      if (vec.equals(entity.source.position, position, 1e-12)) continue;

      next = updateEntity(next, {
        ...entity,
        source: { op: 'free-point', position },
      });
      moved.add(id);
    }
  }

  return { document: next, moved, solutions };
}

function dedupeClusters(clusters: readonly Cluster[]): readonly Cluster[] {
  const seen = new Set<Cluster>();
  const result: Cluster[] = [];
  for (const cluster of clusters) {
    if (seen.has(cluster)) continue;
    seen.add(cluster);
    result.push(cluster);
  }
  return result;
}
